import React, { useState, useEffect } from 'react';
import { authService } from '../services/auth.ts';
import { topicApi } from '../services/api.ts';
import NavigationMenu from './NavigationMenu.tsx';
import ImpactedColleaguesModal from './ImpactedColleaguesModal.tsx';
import TopicRatersModal from './TopicRatersModal.tsx';
import MeetingPrepModal from './MeetingPrepModal.tsx';
import EditTopicModal from './EditTopicModal.tsx';
import ShareFeedbackModal from './ShareFeedbackModal.tsx';
import TruncatedDescription from './TruncatedDescription.tsx';
import ScobyIcon from './icons/ScobyIcon.tsx';
import GitlabIcon from './icons/GitlabIcon.tsx';

interface MyTopicStatsPageProps {
  onBackToMain: () => void;
  onNavigateToPastTopics: () => void;
  onNavigateToDeletedTopics: () => void;
  onNavigateToAbout: () => void;
  onSubmitNewTopic: () => void;
}

const MyTopicStatsPage: React.FC<MyTopicStatsPageProps> = ({
  onBackToMain,
  onNavigateToPastTopics,
  onNavigateToDeletedTopics,
  onNavigateToAbout,
  onSubmitNewTopic
}) => {
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [userInfo, setUserInfo] = useState<{ name: string; username: string } | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [topics, setTopics] = useState<any[]>([]);
  const [colleagues, setColleagues] = useState<any>(null);
  const [showColleaguesModal, setShowColleaguesModal] = useState(false);
  const [showRatersModal, setShowRatersModal] = useState(false);
  const [showMeetingPrepModal, setShowMeetingPrepModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showShareFeedbackModal, setShowShareFeedbackModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState<string | null>(null); // Track which topic's dropdown is open
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMarkPresentedConfirm, setShowMarkPresentedConfirm] = useState(false);
  const [presentedDate, setPresentedDate] = useState('');
  const [markPresentedError, setMarkPresentedError] = useState('');
  const [isMarkingPresented, setIsMarkingPresented] = useState(false);  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<any>(null);
  const [selectedTopicTitle, setSelectedTopicTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionSettingsByTeam, setSessionSettingsByTeam] = useState<{[teamId: string]: any}>({});

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const info = await authService.getUserInfo();
        setUserInfo(info);
      } catch (error) {
        console.error('Error loading user info:', error);
      }
    };
    loadUserInfo();
    loadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = () => {
      if (showDropdown) {
        setShowDropdown(null);
      }
    };

    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsData, topicsData, colleaguesData] = await Promise.all([
        topicApi.getMyStats(),
        topicApi.getMyTopics(50),
        topicApi.getMyImpactedColleagues()
      ]);
      
      setStats(statsData);
      
      // Topics now come with pre-aggregated ratings data from backend
      const topics = topicsData.topics || [];
      
      // Sort topics: scheduled -> future -> past (most recent first)
      const sortedTopics = topics.sort((a: any, b: any) => {
        if (a.sessionType === 'next' && b.sessionType !== 'next') return -1;
        if (b.sessionType === 'next' && a.sessionType !== 'next') return 1;
        if (a.sessionType === 'future' && b.sessionType === 'past') return -1;
        if (b.sessionType === 'future' && a.sessionType === 'past') return 1;
        if (a.sessionType === 'past' && b.sessionType === 'past') {
          return new Date(b.presentedAt).getTime() - new Date(a.presentedAt).getTime();
        }
        return 0;
      });
      
      setTopics(sortedTopics);
      setColleagues(colleaguesData);

      // Load session settings for teams with scheduled topics
      const scheduledTopics = topics.filter((topic: any) => topic.sessionType === 'next');
      const uniqueTeamIds = [...new Set(scheduledTopics.map((topic: any) => topic.teamId))];
      
      if (uniqueTeamIds.length > 0) {
        const sessionSettingsPromises = uniqueTeamIds.map(async (teamId: string) => {
          try {
            const settings = await topicApi.getSessionSettings(teamId);
            return { teamId, settings };
          } catch (error) {
            console.error(`Failed to load session settings for team ${teamId}:`, error);
            return { teamId, settings: null };
          }
        });
        
        const sessionSettingsResults = await Promise.all(sessionSettingsPromises);
        const sessionSettingsMap: {[teamId: string]: any} = {};
        sessionSettingsResults.forEach(({ teamId, settings }) => {
          sessionSettingsMap[teamId] = settings;
        });
        
        setSessionSettingsByTeam(sessionSettingsMap);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load your topic statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    window.location.reload();
  };

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteTopic = async () => {
    if (!selectedTopic) return;
    try {
      setIsDeleting(true);
      await topicApi.deleteTopic(selectedTopic.id);
      loadData(); // Refresh the data
      setShowDeleteConfirm(false);
      setSelectedTopic(null);
    } catch (error) {
      console.error('Error deleting topic:', error);
      alert('Failed to delete topic. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMarkAsPresented = async () => {
    if (!selectedTopic) return;
    if (!presentedDate) {
      setMarkPresentedError('Please select a presentation date');
      return;
    }

    // Validate that the date is not in the future
    const selectedDate = new Date(presentedDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison
    
    if (selectedDate > today) {
      setMarkPresentedError('Presentation date cannot be in the future');
      return;
    }

    setMarkPresentedError('');

    try {
      setIsMarkingPresented(true);
      // Convert selected date to ISO string (use noon UTC to avoid timezone issues)
      const selectedDateTime = new Date(presentedDate + 'T12:00:00.000Z');
      
      // Only send the fields that need to change - don't touch presenters!
      await topicApi.updateTopic(selectedTopic.id, {
        sessionType: 'past',
        presentedAt: selectedDateTime.toISOString()
      });
      loadData(); // Refresh the data
      setShowMarkPresentedConfirm(false);
      setSelectedTopic(null);
      setPresentedDate(''); // Reset for next time
    } catch (error) {
      console.error('Error marking topic as presented:', error);
      setMarkPresentedError('Failed to mark topic as presented. Please try again.');
    } finally {
      setIsMarkingPresented(false);
    }
  };

  // Get default presentation date (session date or current date)
  const getDefaultPresentationDate = (teamId: string) => {
    const sessionSettings = sessionSettingsByTeam[teamId];
    if (sessionSettings?.sessionDate) {
      return sessionSettings.sessionDate; // Already in YYYY-MM-DD format
    }
    return new Date().toISOString().split('T')[0]; // Current date in YYYY-MM-DD
  };

  const formatScheduledDate = (teamId: string) => {
    const sessionSettings = sessionSettingsByTeam[teamId];
    if (!sessionSettings?.sessionDate || !sessionSettings?.sessionTime) {
      return 'Scheduled';
    }
    
    const [year, month, day] = sessionSettings.sessionDate.split('-').map(Number);
    const [hours, minutes] = sessionSettings.sessionTime.split(':').map(Number);
    const utcDateTime = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0, 0));
    
    return (
      <>
        Scheduled:<br />
        {utcDateTime.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        })}
      </>
    );
  };

  if (loading) {
    return (
      <div className="app">
        <div className="header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1>ShareBrief</h1>
            <h2 style={{ margin: 0, color: '#2563eb', fontWeight: '600', fontSize: '1.5rem' }}>
              My Topic Stats
            </h2>
          </div>
        </div>
        <NavigationMenu
        currentTeamId={null}
          currentPage="my-topic-stats"
          onNavigate={(page) => {
            switch (page) {
              case 'main':
                onBackToMain();
                break;
              case 'past-topics':
                onNavigateToPastTopics();
                break;
              case 'deleted-topics':
                onNavigateToDeletedTopics();
                break;
              case 'submit-topic':
                onSubmitNewTopic();
                break;
              case 'about':
                onNavigateToAbout();
                break;
            }
          }}
        />
        <main className="app-main">
          <p>Loading your statistics...</p>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app">
        <div className="header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1>ShareBrief</h1>
            <h2 style={{ margin: 0, color: '#2563eb', fontWeight: '600', fontSize: '1.5rem' }}>
              My Topic Stats
            </h2>
          </div>
        </div>
        <NavigationMenu
        currentTeamId={null}
          currentPage="my-topic-stats"
          onNavigate={(page) => {
            switch (page) {
              case 'main':
                onBackToMain();
                break;
              case 'past-topics':
                onNavigateToPastTopics();
                break;
              case 'deleted-topics':
                onNavigateToDeletedTopics();
                break;
              case 'submit-topic':
                onSubmitNewTopic();
                break;
              case 'about':
                onNavigateToAbout();
                break;
            }
          }}
        />
        <main className="app-main">
          <p style={{ color: 'red' }}>{error}</p>
          <button onClick={loadData}>Retry</button>
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>ShareBrief</h1>
          <h2 style={{ margin: 0, color: '#2563eb', fontWeight: '600', fontSize: '1.5rem' }}>
            My Topic Stats
          </h2>
        </div>
        
        <div style={{ position: 'relative' }}>
          <div
            onClick={() => setShowUserDropdown(!showUserDropdown)}
            style={{
              fontSize: '16px',
              marginBottom: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            Welcome <span style={{ color: '#2563eb', fontWeight: '600' }}>{userInfo?.name || 'User'}</span>
            <span style={{ fontSize: '12px', color: '#666' }}>▼</span>
          </div>
          {showUserDropdown && (
            <div 
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: 1000,
                minWidth: '120px'
              }}
            >
              <button
                onClick={handleLogout}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      <NavigationMenu
        currentTeamId={null}
        currentPage="my-topic-stats"
        onNavigate={(page) => {
          switch (page) {
            case 'main':
              onBackToMain();
              break;
            case 'past-topics':
              onNavigateToPastTopics();
              break;
            case 'deleted-topics':
              onNavigateToDeletedTopics();
              break;
            case 'about':
              onNavigateToAbout();
              break;
            case 'submit-topic':
              onSubmitNewTopic();
              break;
          }
        }}
      />
      
      <main className="app-main">
        {/* Performance Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '32px' }}>
          <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Average Confidence Impact</h3>
            <div style={{ fontSize: '24px', fontWeight: '600', color: '#1e293b' }}>
              {stats?.averageConfidenceImpact || 0}/5 ⭐
            </div>
          </div>
          
          <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Total Hours Saved</h3>
            <div style={{ fontSize: '24px', fontWeight: '600', color: '#1e293b' }}>
              {stats?.totalHoursSaved || 0} 🕐
            </div>
          </div>
          
          <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Total Customers Influenced</h3>
            <div style={{ fontSize: '24px', fontWeight: '600', color: '#1e293b' }}>
              {stats?.totalCustomersInfluenced || 0}
            </div>
          </div>
          
          <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Total Topics Delivered</h3>
            <div style={{ fontSize: '24px', fontWeight: '600', color: '#1e293b' }}>
              {stats?.totalTopicsDelivered || 0}
            </div>
          </div>

          <div 
            style={{ 
              background: '#f8fafc', 
              padding: '20px', 
              borderRadius: '8px', 
              border: '1px solid #e2e8f0',
              cursor: colleagues?.totalColleagues > 0 ? 'pointer' : 'default',
              transition: 'all 0.2s ease'
            }}
            onClick={() => {
              if (colleagues?.totalColleagues > 0) {
                setShowColleaguesModal(true);
              }
            }}
            onMouseEnter={(e) => {
              if (colleagues?.totalColleagues > 0) {
                e.currentTarget.style.borderColor = '#2563eb';
                e.currentTarget.style.backgroundColor = '#f1f5f9';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e2e8f0';
              e.currentTarget.style.backgroundColor = '#f8fafc';
            }}
          >
            <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Colleagues Impacted</h3>
            <div style={{ fontSize: '24px', fontWeight: '600', color: colleagues?.totalColleagues > 0 ? '#2563eb' : '#1e293b' }}>
              {colleagues?.totalColleagues || 0} <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '400' }}>({colleagues?.totalRatings || 0} total ratings)</span>
            </div>
          </div>
        </div>

        {/* Top 3 Topics */}
        {stats?.topTopics && stats.topTopics.length > 0 && (
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ marginBottom: '16px', color: '#1e293b', fontSize: '18px', fontWeight: '600' }}>🏆 Your Top 3 Topics</h3>
            <div style={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              {stats.topTopics.map((topic: any, index: number) => (
                <div key={topic.id} style={{
                  backgroundColor: '#ffffff',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                  position: 'relative',
                  flex: '1 1 300px',
                  maxWidth: '400px'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '-8px',
                    left: '16px',
                    backgroundColor: index === 0 ? '#fbbf24' : index === 1 ? '#9ca3af' : '#cd7c2f',
                    color: 'white',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    #{index + 1}
                  </div>
                  
                  <h4 style={{ margin: '8px 0 8px 0', color: '#1e293b', fontSize: '16px' }}>{topic.title}</h4>
                  <p style={{ fontSize: '15px', color: '#64748b', marginBottom: '8px' }}>
                    <TruncatedDescription description={topic.description} topicId={topic.id} />
                  </p>
                  <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                    <strong>Presented:</strong> {new Date(topic.presentedAt).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ fontSize: '15px' }}>
                      <span style={{ color: '#f59e0b' }}>
                        {topic.avgUsefulnessRating ? `${topic.avgUsefulnessRating.toFixed(1)}/5 ⭐` : 'No rating'}
                      </span>
                    </div>
                    <div style={{ fontSize: '15px', color: '#059669' }}>
                      {topic.totalCustomers || 0} customers influenced
                    </div>
                    <div style={{ fontSize: '15px', color: '#1976d2' }}>
                      🕒 {topic.totalHoursSaved ? `${topic.totalHoursSaved.toFixed(1)} hrs saved` : '0 hrs saved'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Topics Table */}
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>My Topics</h3>
          {topics.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
              <p>You haven't presented any topics yet. Ready to share your expertise?</p>
              <button 
                onClick={onSubmitNewTopic}
                style={{ 
                  background: '#2563eb', 
                  color: 'white', 
                  border: 'none', 
                  padding: '12px 24px', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Submit your first topic
              </button>
              <p style={{ margin: '8px 0 0 0' }}>and start building your impact stats!</p>
            </div>
          ) : (
            <div className="topic-table-container" style={{ overflow: 'visible' }}>
              <table className="topic-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: '290px' }}>Topic</th>
                    <th style={{ minWidth: '110px' }}>Series</th>
                    <th style={{ minWidth: '110px' }}>Presented</th>
                    <th style={{ textAlign: 'center' }}>Attendees</th>
                    <th style={{ textAlign: 'center' }}>Confidence Impact</th>
                    <th style={{ textAlign: 'center' }}>Prep Hours Saved</th>
                    <th style={{ textAlign: 'center' }}>Customers Influenced</th>
                    <th style={{ textAlign: 'center' }}>Unique Ratings</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {topics.map((topic, index) => (
                    <tr key={topic.id} className="topic-row">
                      <td style={{ minWidth: '290px' }}>
                        <div className="topic-title" style={{ maxWidth: 'none' }}>{topic.title}</div>
                        <div className="topic-description" style={{ maxWidth: 'none', marginLeft: '16px' }}>
                          <TruncatedDescription description={topic.description} topicId={topic.id} />
                        </div>
                      </td>
                      <td style={{ minWidth: '110px' }}>{topic.teamName}</td>
                      <td style={{ minWidth: '110px' }}>
                        {topic.sessionType === 'past' ? 
                          new Date(topic.presentedAt).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          }) : 
                          topic.sessionType === 'next' ? formatScheduledDate(topic.teamId) : 'Future Session'
                        }
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {topic.sessionType === 'past' ? (topic.attendeeCount || '-') : '-'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {topic.averageUsefulnessRating ? `${topic.averageUsefulnessRating.toFixed(1)}/5` : '-'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {topic.totalHoursSaved || '-'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {topic.totalCustomerConversations || '-'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span 
                          style={{ 
                            color: topic.ratingsCount > 0 ? '#2563eb' : '#64748b', 
                            cursor: topic.ratingsCount > 0 ? 'pointer' : 'default'
                          }}
                          onClick={() => {
                            if (topic.ratingsCount > 0) {
                              setSelectedTopicId(topic.id);
                              setSelectedTopicTitle(topic.title);
                              setShowRatersModal(true);
                            }
                          }}
                        >
                          {topic.ratingsCount || 0}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
                          {/* Scoby icon */}
                          {topic.builderSpaceUrl && topic.builderSpaceUrl.trim() && (
                            <button
                              onClick={() => window.open(topic.builderSpaceUrl, '_blank')}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '16px',
                                padding: '4px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                              onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                              title="Scoby Project"
                            >
                              <ScobyIcon />
                            </button>
                          )}

                          {/* Gitlab icon */}
                          {topic.gitlabUrl && topic.gitlabUrl.trim() && (
                            <button
                              onClick={() => window.open(topic.gitlabUrl, '_blank')}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '16px',
                                padding: '4px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                              onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                              title="Gitlab Project"
                            >
                              <GitlabIcon />
                            </button>
                          )}

                          {/* Play button for topics with recording URLs */}
                          {topic.recordingUrl && (topic.sessionType === 'next' || topic.sessionType === 'past') && (
                            <button
                              onClick={() => window.open(topic.recordingUrl, '_blank')}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '16px',
                                padding: '4px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                              onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                              title="Recording"
                            >
                              ▶
                            </button>
                          )}
                          
                          {/* Meeting prep notes icon for scheduled and past topics */}
                          {(topic.sessionType === 'next' || topic.sessionType === 'past') && (
                            <button
                              onClick={() => setShowMeetingPrepModal(true)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '16px',
                                padding: '4px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                              onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                              title="Generate meeting prep notes"
                            >
                              ✦
                            </button>
                          )}
                          
                          {/* 3-dots menu */}
                          <div style={{ position: 'relative' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowDropdown(showDropdown === topic.id ? null : topic.id);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '16px',
                                padding: '4px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                              onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                              title="Topic actions"
                            >
                              ⋮
                            </button>
                            {showDropdown === topic.id && (
                              <div 
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  position: 'absolute',
                                  top: index >= topics.length - 2 ? 'auto' : '100%',
                                  bottom: index >= topics.length - 2 ? '100%' : 'auto',
                                  right: '0',
                                  backgroundColor: 'white',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                  zIndex: 9999,
                                  minWidth: '150px'
                                }}
                              >
                                <button
                                  onClick={() => {
                                    setSelectedTopic(topic);
                                    setShowEditModal(true);
                                    setShowDropdown(null);
                                  }}
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: 'none',
                                    background: 'none',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                  }}
                                  onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                                  onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                                >
                                  Edit
                                </button>
                                {topic.sessionType !== 'future' && (
                                  <button
                                    onClick={() => {
                                      setSelectedTopic(topic);
                                      setShowShareFeedbackModal(true);
                                      setShowDropdown(null);
                                    }}
                                    style={{
                                      display: 'block',
                                      width: '100%',
                                      padding: '8px 12px',
                                      border: 'none',
                                      background: 'none',
                                      textAlign: 'left',
                                      cursor: 'pointer',
                                      fontSize: '14px'
                                    }}
                                    onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                                    onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                                  >
                                    Share feedback link
                                  </button>
                                )}
                                {(topic.sessionType === 'next' || topic.sessionType === 'future') && (
                                  <button
                                    onClick={() => {
                                      setSelectedTopic(topic);
                                      setPresentedDate(getDefaultPresentationDate(topic.teamId)); // Set default date
                                      setShowMarkPresentedConfirm(true);
                                      setShowDropdown(null);
                                    }}
                                    style={{
                                      display: 'block',
                                      width: '100%',
                                      padding: '8px 12px',
                                      border: 'none',
                                      background: 'none',
                                      textAlign: 'left',
                                      cursor: 'pointer',
                                      fontSize: '14px'
                                    }}
                                    onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                                    onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                                  >
                                    Mark as Presented
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    setSelectedTopic(topic);
                                    setShowDeleteConfirm(true);
                                    setShowDropdown(null);
                                  }}
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: 'none',
                                    background: 'none',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: '#dc3545'
                                  }}
                                  onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                                  onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                                >
                                  Delete Topic
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <ImpactedColleaguesModal
        isOpen={showColleaguesModal}
        onClose={() => setShowColleaguesModal(false)}
        colleagues={colleagues?.colleagues || []}
      />

      <TopicRatersModal
        isOpen={showRatersModal}
        onClose={() => setShowRatersModal(false)}
        topicId={selectedTopicId}
        topicTitle={selectedTopicTitle}
      />

      {showMeetingPrepModal && (
        <MeetingPrepModal
          onClose={() => setShowMeetingPrepModal(false)}
        />
      )}

      {showEditModal && selectedTopic && (
        <EditTopicModal
          topic={selectedTopic}
          onClose={() => {
            setShowEditModal(false);
            setSelectedTopic(null);
          }}
          onTopicUpdated={() => {
            // Refresh the topics list
            loadData();
            setShowEditModal(false);
            setSelectedTopic(null);
          }}
        />
      )}

      {showShareFeedbackModal && selectedTopic && (
        <ShareFeedbackModal
          topic={selectedTopic}
          onClose={() => {
            setShowShareFeedbackModal(false);
            setSelectedTopic(null);
          }}
        />
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Topic</h3>
            <p>Are you sure you want to delete "{selectedTopic?.title}"?</p>
            <p>This topic will be moved to the deleted topics page where it can be restored if needed.</p>
            <div className="modal-actions">
              <button onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button onClick={handleDeleteTopic} disabled={isDeleting} style={{ backgroundColor: isDeleting ? '#6c757d' : '#dc3545', color: 'white' }}>
                {isDeleting ? 'Deleting...' : 'Delete Topic'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMarkPresentedConfirm && (
        <div className="modal-overlay" onClick={() => setShowMarkPresentedConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Mark as Presented</h3>
            <p>Mark <strong style={{ color: '#333' }}>"{selectedTopic?.title}"</strong> as presented and move to past topics.</p>
            
            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
              <label htmlFor="presented-date-stats" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Presentation Date:
              </label>
              <input
                id="presented-date-stats"
                type="date"
                value={presentedDate}
                onChange={(e) => setPresentedDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]} // Prevent future dates
                style={{
                  width: '180px',
                  padding: '8px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              />
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {selectedTopic && sessionSettingsByTeam[selectedTopic.teamId]?.sessionDate 
                  ? `Session date: ${new Date(sessionSettingsByTeam[selectedTopic.teamId].sessionDate + 'T00:00:00Z').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`
                  : 'No session date set'}
              </div>
            </div>

            {markPresentedError && <div className="error-message" style={{ color: '#dc3545', fontSize: '14px', marginTop: '12px' }}>{markPresentedError}</div>}

            <div className="modal-actions">
              <button onClick={() => {
                setShowMarkPresentedConfirm(false);
                setSelectedTopic(null);
                setPresentedDate('');
                setMarkPresentedError('');
              }}>Cancel</button>
              <button onClick={handleMarkAsPresented} disabled={isMarkingPresented} style={{ backgroundColor: isMarkingPresented ? '#6c757d' : '#28a745', color: 'white' }}>
                {isMarkingPresented ? 'Marking...' : 'Mark as Presented'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTopicStatsPage;
